import axios, { type AxiosResponse } from 'axios'
import { isXpayType, PaymentTypes, toBackendPaymentType } from 'commons/constants/paymentTypes'
import type {
	PaymentFlowResponse,
	PaymentRequestResponse,
	VerifyIdentityResponse
} from 'commons/types/paymentTypes'
import type { PaymentRedirectResponse, WalletBillingAddress } from 'commons/types/paymentTypes'
import { CountryISO } from 'commons/constants/countries'
import { extractRequestPubUuidFromUrl } from 'commons/utils/urlUtils'

const handleCreatedPaymentRequest = (
	response: AxiosResponse<PaymentRequestResponse>,
	paymentType: PaymentTypes,
	amount: number,
	onShowPaymentModal?: (requestPubUuid: string, amount: number) => void
) => {
	if ((isXpayType(paymentType) || paymentType === PaymentTypes.CARD) && onShowPaymentModal) {
		const requestPubUuid =
			response.data?.success === true
				? typeof response.data.request_pub_uuid === 'string'
					? response.data.request_pub_uuid
					: null
				: extractRequestPubUuidFromUrl(response.request?.responseURL || '')
		if (requestPubUuid) {
			onShowPaymentModal(requestPubUuid, amount)
			return
		}
	}
	const url =
		response.data?.success === true && typeof response.data.url === 'string'
			? response.data.url
			: response.request?.responseURL
	if (!url) throw new Error('Missing payment request URL')
	window.location.href = url
}

export const postLydiaPageRequest = async (
	amount: number,
	paymentType: PaymentTypes,
	motif: string,
	phone: string,
	email: string,
	tag: string,
	onShowVerifyIdentityModal?: (requestPubUuid: string, amount: number) => void
) => {
	const formData = new FormData()
	formData.append('amount', amount.toString())
	formData.append('price', amount.toString())
	formData.append('slug', '0')
	formData.append('payment_method', toBackendPaymentType(paymentType))
	if (isXpayType(paymentType) || paymentType === PaymentTypes.CARD) {
		formData.append('response_format', 'json')
	}
	formData.append('collector_desc', motif)
	formData.append('customdata[recipient]', phone)
	formData.append('customdata[email-key]', email)
	formData.append('payment', 'on')
	formData.append('source', 'payment_page')
	try {
		const url = `${process.env.REACT_APP_POTS_URL}collect/createrequestlydiapage?t=${tag}`
		const response = await axios.post<PaymentRequestResponse>(url, formData, {
			withCredentials: true,
			timeout: 15000,
			headers: {
				Accept: 'application/json'
			}
		})
		if (response.data.error_message) {
			throw new Error(response.data.error_message)
		}
		handleCreatedPaymentRequest(response, paymentType, amount, onShowVerifyIdentityModal)
	} catch (error) {
		console.error('Error posting Lydia page request:', error)
		throw error
	}
}

export const postMoneyPotRequest = async (
	amount: number,
	paymentType: PaymentTypes,
	phone: string,
	email: string,
	name: string,
	slug: string,
	onShowVerifyIdentityModal?: (requestPubUuid: string, amount: number) => void
) => {
	const formData = new FormData()
	formData.append('price', amount.toString())
	formData.append('slug', slug)
	formData.append('payment_method', toBackendPaymentType(paymentType))
	if (isXpayType(paymentType) || paymentType === PaymentTypes.CARD) {
		formData.append('response_format', 'json')
	}
	formData.append('customdata[amount-key]', amount.toString())
	if (paymentType === PaymentTypes.CARD || isXpayType(paymentType)) {
		formData.append('customdata[email-key]', email)
		formData.append('customdata[name-key]', name)
	} else if (paymentType === PaymentTypes.LYDIA) {
		formData.append('customdata[recipient-key]', phone)
	}
	try {
		const response = await axios.post<PaymentRequestResponse>(
			`${process.env.REACT_APP_POTS_URL}collect/createrequest?id=${slug}`,
			formData
		)
		if (response.data.error_message) {
			// SA-2875: Show generic error message instead of invalid amount error message
			if (response.data.error_message === 'Veuillez saisir un montant valide.') {
				throw new Error('An error occurred, please try again later')
			} else {
				throw new Error(response.data.error_message)
			}
		}
		handleCreatedPaymentRequest(response, paymentType, amount, onShowVerifyIdentityModal)
	} catch (error) {
		console.error('Error posting Lydia page request:', error)
		throw error
	}
}

export const loadPaymentFlow = async (
	firstname: string,
	lastname: string,
	requestPubUuid: string,
	paymentType: PaymentTypes,
	lang: string,
	payerAddress?: Pick<WalletBillingAddress, 'city' | 'country' | 'zipcode'>
): Promise<PaymentFlowResponse> => {
	try {
		const paymentTypeUrl = isXpayType(paymentType) ? 'cko_flow' : 'cb_form'
		const response: AxiosResponse<PaymentFlowResponse> = await axios.get(
			`${process.env.REACT_APP_POTS_URL}collect/${paymentTypeUrl}`,
			{
				params: {
					firstname,
					lastname,
					request_pub_uuid: requestPubUuid,
					lang: lang,
					...(payerAddress && {
						city: payerAddress.city,
						country_alpha_2: payerAddress.country,
						zipcode: payerAddress.zipcode
					})
				}
			}
		)
		if (response.request.responseURL.indexOf('payment-error') === -1) {
			return response.data
		} else {
			window.location.href = response.request.responseURL
			return {
				success: false,
				environment: 'sandbox',
				public_key: ''
			}
		}
	} catch (error) {
		console.error('Error loading CKO flow:', error)
		return {
			success: false,
			environment: 'sandbox',
			public_key: ''
		}
	}
}

export const verifyIdentity = async (
	firstname: string,
	lastname: string,
	requestPubUuid: string,
	birthdate: string | null = null
): Promise<VerifyIdentityResponse> => {
	try {
		const response = await axios.get(`${process.env.REACT_APP_POTS_URL}collect/verify_identity`, {
			params: {
				firstname,
				lastname,
				request_pub_uuid: requestPubUuid,
				...(birthdate && { birthdate })
			}
		})
		return {
			success: response.data?.success === true,
			message: response.data?.message
		}
	} catch (error) {
		console.error('Error verifying identity:', error)
		// On failure, return success: false to trigger birthdate step
		return {
			success: false,
			message: 'An error occurred during verification'
		}
	}
}

export const initializeWalletPayment = async (
	requestPubUuid: string,
	lang: string
): Promise<PaymentFlowResponse> => {
	const response: AxiosResponse<PaymentFlowResponse> = await axios.post(
		`${process.env.REACT_APP_POTS_URL}collect/cko_flow_initialize`,
		new URLSearchParams({
			request_pub_uuid: requestPubUuid,
			country_alpha_2: CountryISO.FR,
			lang
		})
	)
	if (response.request?.responseURL?.includes('payment-error')) {
		window.location.assign(response.request.responseURL)
		throw new Error('Wallet initialization rejected')
	}
	return response.data
}

export const submitWalletPayment = async (
	requestPubUuid: string,
	token: string,
	billingAddress: WalletBillingAddress | null,
	lang: string
): Promise<PaymentRedirectResponse> => {
	if (!token.trim()) throw new Error('Missing wallet token')
	const response: AxiosResponse<PaymentRedirectResponse> = await axios.get(
		`${process.env.REACT_APP_POTS_URL}collect/cko_flow`,
		{
			params: {
				request_pub_uuid: requestPubUuid,
				cko_token: token,
				firstname: billingAddress?.firstname || '',
				lastname: billingAddress?.lastname || '',
				address: [billingAddress?.address1, billingAddress?.address2, billingAddress?.address3]
					.filter(Boolean)
					.join(' '),
				city: billingAddress?.city,
				country_alpha_2: billingAddress?.country,
				zipcode: billingAddress?.zipcode,
				lang
			}
		}
	)
	if (response.request?.responseURL?.includes('payment-error')) {
		window.location.assign(response.request.responseURL)
		throw new Error('Wallet payment rejected')
	}
	if (
		response.data?.success !== true ||
		response.data.error ||
		typeof response.data.url !== 'string' ||
		!response.data.url.trim()
	) {
		throw new Error('Missing wallet payment redirect')
	}
	return response.data
}
